import { ThemeToggle } from '@app/components/layout/sidenav/footer/ThemeToggle';
import { SIDENAV_TOKENS } from '@app/components/layout/sidenav/sidenav-tokens';
import { useIsImpersonationOn } from '@app/core/auth/impersonation/hooks/useIsImpersonationOn';
import { SettingsRoutes } from '@app/modules/settings/routing/SettingsRoutes';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { SidenavAccountMenuItem } from './SidenavAccountMenuItem';
import { SidenavUserAvatar } from './SidenavUserAvatar';
import { useSidenavAccountIdentity } from './useSidenavAccountIdentity';

const settingsMessage = defineMessage({
  id: 'core.sidenav.settingsBtnText',
  defaultMessage: 'Configuración de usuario',
});

const logoutMessage = defineMessage({
  id: 'core.sidenav.logoutBtnText',
  defaultMessage: 'Cerrar sesión',
});

const changeUserMessage = defineMessage({
  id: 'auth.userInfo.changeUser',
  defaultMessage: 'Cambiar usuario',
});

/**
 * Full-width rule without vertical spacing.
 * MenuItem injects theme.spacing(1) margin on a following Divider; !important clears it.
 * Rendered as a div so Bootstrap's global `hr` 1rem margin does not apply.
 */
const flushDividerSx = {
  borderBottomWidth: 'thin',
  my: '0 !important',
} as const;

const clientChipSx = {
  maxWidth: '100%',
  height: 22,
  '& .MuiChip-label': {
    typography: 'caption',
    px: 0.75,
  },
  '& .MuiChip-avatar': {
    width: 16,
    height: 16,
    ml: '4px',
  },
} as const;

const ellipsisNowrapSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const displayNameSx = {
  fontWeight: 500,
  ...ellipsisNowrapSx,
} as const;

const emailSx = {
  display: 'block',
  ...ellipsisNowrapSx,
} as const;

const accountPopoverPaperSx = {
  bgcolor: 'background.paper',
  color: 'text.primary',
  minWidth: SIDENAV_TOKENS.drawerWidth,
  mt: 0.5,
} as const;

export type SidenavAccountPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

/**
 * Click-triggered account menu: user + client identity, settings, theme, logout.
 */
export function SidenavAccountPopover({ open, anchorEl, onClose }: SidenavAccountPopoverProps) {
  const { client, isSuperadmin, avatarSrc, initials, email, clientLabel, displayNameLong } =
    useSidenavAccountIdentity();
  const isImpersonating = useIsImpersonationOn();
  const $t = useIntl().formatMessage;
  const userAvatarSize = SIDENAV_TOKENS.accountPopoverAvatarSize;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      marginThreshold={0}
      slotProps={{
        paper: {
          sx: accountPopoverPaperSx,
        },
      }}
    >
      <Box sx={{ py: 0.5 }}>
        <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            data-cy="auth-client-name"
            label={clientLabel}
            avatar={
              client?.logoUrl ? (
                <Avatar src={client.logoUrl} alt="" data-cy="auth-client-logo" />
              ) : undefined
            }
            sx={clientChipSx}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pb: 1 }}>
          <SidenavUserAvatar
            size={userAvatarSize}
            src={avatarSrc}
            initials={initials}
            initialsSx={{ typography: 'caption', color: 'common.white' }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={displayNameSx}>
              {displayNameLong}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={emailSx}>
              {email}
            </Typography>
          </Box>
          {!isSuperadmin && (
            <Tooltip title={$t(settingsMessage)}>
              <IconButton
                component={Link}
                to="/settings/user"
                onClick={onClose}
                size="small"
                sx={{ flexShrink: 0, color: 'text.secondary', ml: 1.5 }}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider component="div" sx={flushDividerSx} />

        <Box sx={{ px: 1.5, py: 1 }}>
          <ThemeToggle />
        </Box>

        <Divider component="div" sx={flushDividerSx} />
        <SidenavAccountMenuItem
          to="/logout"
          onClick={onClose}
          icon={<LogoutOutlinedIcon fontSize="small" />}
          label={$t(logoutMessage)}
        />
        {isImpersonating && (
          <>
            <Divider component="div" sx={flushDividerSx} />
            <SidenavAccountMenuItem
              to={SettingsRoutes.ROUTES.impersonation}
              onClick={onClose}
              icon={<AdminPanelSettingsIcon fontSize="small" />}
              label={$t(changeUserMessage)}
            />
          </>
        )}
      </Box>
    </Popover>
  );
}
